import { useEffect, useState } from 'react'
import { useStore } from '../store/useStore'
import { api } from '../lib/api'
import { MatchPreferences } from '../store/types'

interface MatchSettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function MatchSettingsModal({ isOpen, onClose }: MatchSettingsModalProps) {
  const { matchPreferences, setMatchPreferences } = useStore()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Local state for UI, initialized from store or defaults
  const [isWaiting, setIsWaiting] = useState(matchPreferences?.is_active || false)
  const [timezone, setTimezone] = useState(
    matchPreferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  )

  // Fetch preferences when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchPreferences = async () => {
        setIsLoading(true)
        setError(null)
        try {
          const result = await api.matchPreferences.get()
          if (result && result.preference) {
            const prefs = result.preference as MatchPreferences;
            setMatchPreferences(prefs)
            setIsWaiting(prefs.is_active)
            setTimezone(prefs.timezone)
          }
        } catch (err) {
          console.error('Failed to fetch match preferences:', err)
          setError('설정을 불러오는 데 실패했습니다.')
        } finally {
          setIsLoading(false)
        }
      }
      fetchPreferences()
    }
  }, [isOpen, setMatchPreferences])

  // Sync local state if store changes from another source
  useEffect(() => {
    setIsWaiting(matchPreferences?.is_active || false)
    setTimezone(matchPreferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone)
  }, [matchPreferences])


  const handleSave = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await api.matchPreferences.toggle(isWaiting ? 1 : 0, timezone)
      if (result && result.preference) {
        setMatchPreferences(result.preference as MatchPreferences)
      }
      onClose()
    } catch (err) {
      console.error('Failed to save match preferences:', err)
      setError('설정을 저장하는 데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) {
    return null
  }

  // A more comprehensive list of timezones would be better in a real app
  const timezones = [
    'UTC',
    'Asia/Seoul',
    'Asia/Tokyo',
    'America/New_York',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Australia/Sydney',
  ].filter(tz => tz !== timezone);

  return (
    <div className="modal modal-open">
      <div className="modal-box">
        <button onClick={onClose} className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg">매칭 설정</h3>
        
        {isLoading && <div className="flex justify-center p-8"><span className="loading loading-lg loading-spinner"></span></div>}
        
        {!isLoading && (
          <div className="py-4">
            <div className="form-control">
              <label className="label cursor-pointer">
                <span className="label-text">매칭 대기</span>
                <input 
                  type="checkbox" 
                  className="toggle toggle-primary" 
                  checked={isWaiting}
                  onChange={(e) => setIsWaiting(e.target.checked)} 
                />
              </label>
              <p className="text-xs text-base-content/60 px-1">
                매칭 대기를 활성화하면 다음 배치부터 추천을 받게 됩니다.
              </p>
            </div>

            <div className="form-control mt-4">
              <label className="label">
                <span className="label-text">타임존</span>
              </label>
              <select 
                className="select select-bordered w-full"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                <option value={timezone}>{timezone}</option>
                {timezones.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
               <p className="text-xs text-base-content/60 px-1 mt-1">
                배치 매칭은 설정된 타임존을 기준으로 진행됩니다.
              </p>
            </div>

            {error && <div className="text-error text-sm mt-4">{error}</div>}
          </div>
        )}

        <div className="modal-action">
          <button onClick={onClose} className="btn" disabled={isLoading}>취소</button>
          <button onClick={handleSave} className="btn btn-primary" disabled={isLoading}>
            {isLoading && <span className="loading loading-spinner loading-sm"></span>}
            저장
          </button>
        </div>
      </div>
       <div className="modal-backdrop" onClick={onClose}></div>
    </div>
  )
}
